import { Award, Heart, Users, Target, Leaf, Clock, Sparkles, ShieldCheck, Building2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Quality First',
      description: 'Every batch is evaluated for aroma, texture, and color before dispatch.',
    },
    {
      icon: Heart,
      title: 'Craft with Care',
      description: 'Traditional handling combined with disciplined modern hygiene practices.',
    },
    {
      icon: Users,
      title: 'Customer Focused',
      description: 'Built around real kitchen needs, from home cooks to food businesses.',
    },
    {
      icon: Target,
      title: 'Mission Driven',
      description: 'Our mission is simple: authentic Indian flavor with consistent quality.',
    },
    {
      icon: Leaf,
      title: 'Responsible Sourcing',
      description: 'We prefer trusted supply partners and process with minimal wastage.',
    },
    {
      icon: Clock,
      title: 'Process Discipline',
      description: 'Small-lot processing and controlled packing preserve natural freshness.',
    },
  ];

  const timeline = [
    'Source from trusted distribution channels and partner networks.',
    'Clean, sort, and process lots with strict quality checks.',
    'Pack securely and dispatch with freshness-safe handling.',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-24 pb-16">
      <section className="py-14 bg-gradient-to-br from-amber-900 to-orange-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-white/20 text-white border-white/30 mb-4 backdrop-blur-sm">About Us</Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-5 tracking-tight">
              Built on <span className="text-amber-300">Purity, Process, and Trust</span>
            </h1>
            <p className="text-lg text-amber-100 leading-relaxed">
              We are building a quality-first spice brand for Indian kitchens and modern food businesses.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <Badge className="bg-amber-100 text-amber-900 border-amber-300">Our Story</Badge>
            <h2 className="text-4xl font-bold text-amber-950 leading-tight">
              A Serious Standard for Everyday Spices
            </h2>
            <p className="text-amber-800 leading-relaxed text-lg">
              GWAL Spices started with one clear objective: provide clean, authentic spices without compromise.
              Our approach combines practical sourcing discipline, controlled processing, and customer-first delivery.
            </p>
            <p className="text-amber-800 leading-relaxed text-lg">
              We focus on what matters most in real kitchens: dependable flavor, freshness retention, and transparent quality.
            </p>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="bg-white border border-amber-200 rounded-xl p-3">
                <p className="text-sm font-semibold text-amber-950 inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-700" />Quality focused</p>
              </div>
              <div className="bg-white border border-amber-200 rounded-xl p-3">
                <p className="text-sm font-semibold text-amber-950 inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-700" />Freshness led</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-7 shadow-sm">
            <h3 className="text-2xl font-bold text-amber-950 mb-5 inline-flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-700" />
              How We Operate
            </h3>
            <div className="space-y-4">
              {timeline.map((step, idx) => (
                <div key={step} className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-amber-700 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                    {idx + 1}
                  </div>
                  <p className="text-amber-800 text-sm leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-y border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-amber-100 text-amber-900 border-amber-300 mb-4">Our Values</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-950">What Drives Us Daily</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value) => (
              <Card
                key={value.title}
                className="group border border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all duration-300 bg-white"
              >
                <CardContent className="p-7 space-y-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-amber-950">{value.title}</h3>
                  <p className="text-amber-700 text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-amber-900 to-orange-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-5">Cook with Authentic Flavor Confidence</h2>
          <p className="text-lg text-amber-100 mb-8">
            Explore our product range and experience quality-led spices crafted for consistency.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/shop"
              className="inline-block px-8 py-4 bg-white text-amber-900 rounded-lg font-semibold text-base hover:bg-amber-50 transition-colors shadow-xl"
            >
              Explore Products
            </Link>
            <a
              href="https://wa.me/919063784294"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 border border-white text-white rounded-lg font-semibold text-base hover:bg-white/10 transition-colors"
            >
              Contact Team
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
